import { zodResolver } from '@hookform/resolvers/zod';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { useDeleteAddonMutation, useUpdateAddonMutation } from 'src/apiClients';
import type { Addon } from 'src/apiClients/queries';
import { ConfirmDiscardDialog } from 'src/pages/instance-detail-page/instance-detail-form/confirm-discard-dialog/ConfirmDiscardDialog';
import { InfoField } from 'src/pages/instance-detail-page/instance-detail-form/info-field/InfoField';

import { AddonFormFields } from '../addon-form-fields/AddonFormFields';
import type { AddonFormInput, AddonFormValues } from '../addon-form-schema';
import { ADDON_TYPES, addonFormSchema, submitAddonSchema } from '../addon-form-schema';
import { SUBSCRIPTION_PRODUCT_NAMES } from '../subscription-product-names';
import { i18n } from './EditAddonModal.i18n';
import { useStyles } from './EditAddonModal.styles';

export interface EditAddonModalProps {
    addon: Addon;
    subscriptionId: string;
    open: boolean;
    onClose: () => void;
}

export const EditAddonModal = ({ addon, subscriptionId, open, onClose }: EditAddonModalProps) => {
    const intl = useIntl();
    const { classes } = useStyles();
    const { enqueueSnackbar } = useSnackbar();
    const [updateAddon, { isLoading: isSaving }] = useUpdateAddonMutation();
    const [deleteAddon, { isLoading: isDeleting }] = useDeleteAddonMutation();
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

    const form = useForm<AddonFormInput, unknown, AddonFormValues>({
        resolver: zodResolver(submitAddonSchema),
        values: addonFormSchema.parse(addon),
        mode: 'onChange',
    });
    const {
        handleSubmit,
        reset,
        watch,
        formState: { isDirty },
    } = form;

    useEffect(() => {
        if (open) reset(addonFormSchema.parse(addon));
    }, [open, addon, reset]);

    const onSubmit = handleSubmit(async (values) => {
        try {
            await updateAddon({ id: addon.id, subscriptionId, ...values }).unwrap();
            enqueueSnackbar(intl.formatMessage(i18n.saveSuccess), { variant: 'success' });
            onClose();
        } catch {
            enqueueSnackbar(intl.formatMessage(i18n.saveError), { variant: 'error' });
        }
    });

    const handleDelete = async () => {
        if (isDeleting) return;
        try {
            await deleteAddon({ id: addon.id, subscriptionId }).unwrap();
            enqueueSnackbar(intl.formatMessage(i18n.deleteSuccess), { variant: 'success' });
            setDeleteConfirmOpen(false);
            onClose();
        } catch {
            enqueueSnackbar(intl.formatMessage(i18n.deleteError), { variant: 'error' });
        }
    };

    const isBusy = isSaving || isDeleting;
    const selectedProductCode = watch('name');
    const fallbackProductCode =
        selectedProductCode && !SUBSCRIPTION_PRODUCT_NAMES[selectedProductCode]
            ? selectedProductCode
            : undefined;
    const fallbackType =
        addon.type && !ADDON_TYPES.find((t) => t.value === addon.type) ? addon.type : undefined;

    return (
        <>
            <Dialog
                open={open}
                onClose={isBusy ? undefined : onClose}
                maxWidth="sm"
                fullWidth
                automation-id="edit-addon-modal"
            >
                <DialogTitle automation-id="edit-addon-modal-title" className={classes.title}>
                    {intl.formatMessage(i18n.title)}
                </DialogTitle>
                <DialogContent>
                    <Divider className={classes.divider} />
                    <div className={classes.readOnlyGrid}>
                        <InfoField
                            label={intl.formatMessage(i18n.productCode)}
                            value={selectedProductCode}
                            automationId="addon-product-code"
                        />
                        <div className={classes.serialField}>
                            <InfoField
                                label={intl.formatMessage(i18n.serialNumber)}
                                value={addon.id}
                                automationId="addon-serial-number"
                            />
                        </div>
                    </div>
                    <Divider className={classes.divider} />
                    <AddonFormFields
                        form={form}
                        fallbackProductCode={fallbackProductCode}
                        fallbackType={fallbackType}
                    />
                </DialogContent>
                <DialogActions>
                    <div className={classes.deleteZone}>
                        <Button
                            color="error"
                            onClick={() => setDeleteConfirmOpen(true)}
                            disabled={isBusy}
                            automation-id="btn-delete-addon"
                        >
                            {intl.formatMessage(i18n.deleteButton)}
                        </Button>
                    </div>
                    <Button onClick={onClose} disabled={isBusy} automation-id="btn-cancel-addon">
                        {intl.formatMessage(i18n.cancel)}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={onSubmit}
                        disabled={!isDirty || isBusy}
                        automation-id="btn-save-addon"
                    >
                        {intl.formatMessage(i18n.save)}
                    </Button>
                </DialogActions>
            </Dialog>
            <ConfirmDiscardDialog
                open={deleteConfirmOpen}
                title={intl.formatMessage(i18n.deleteConfirmTitle)}
                message={intl.formatMessage(i18n.deleteConfirmMessage)}
                discardLabel={intl.formatMessage(i18n.deleteConfirmLabel)}
                keepEditingLabel={intl.formatMessage(i18n.deleteCancelLabel)}
                onDiscard={handleDelete}
                onKeepEditing={() => setDeleteConfirmOpen(false)}
                isLoading={isDeleting}
            />
        </>
    );
};
